import { useMemo } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { useAtomValue } from 'jotai';

import { getFileFolderId, toFileNode } from '../helpers/fileSystemNode.js';
import { getDocumentsOption } from '../queries/rag.js';
import { draftFoldersAtom, fileLocationsAtom } from '../store/fileSystem.js';
import {
  MAX_FOLDER_DEPTH,
  type FileNode,
  type FolderNode,
} from '../types/fileSystem.js';

type UseFileSystemNodesParams = {
  folderId: string;
  depth: number;
  search?: string;
};

export const useFileSystemNodes = ({
  folderId,
  depth,
  search = '',
}: UseFileSystemNodesParams) => {
  const draftFolders = useAtomValue(draftFoldersAtom);
  const fileLocations = useAtomValue(fileLocationsAtom);
  const { data: documents = [], isLoading } = useQuery(getDocumentsOption());

  const query = search.trim().toLowerCase();

  const folders = useMemo<FolderNode[]>(() => {
    const children = draftFolders.filter(
      (folder) => folder.parentId === folderId,
    );

    if (depth >= MAX_FOLDER_DEPTH) {
      if (children.length && import.meta.env.DEV) {
        console.warn(
          `[knowledge-base] dropped ${children.length} folder(s) below the maximum depth of ${MAX_FOLDER_DEPTH}`,
        );
      }

      return [];
    }

    return children.filter((folder) =>
      folder.name.toLowerCase().includes(query),
    );
  }, [draftFolders, folderId, depth, query]);

  const files = useMemo<FileNode[]>(() => {
    return documents
      .map(toFileNode)
      .filter((file) => getFileFolderId(file.id, fileLocations) === folderId)
      .filter((file) => file.fileName.toLowerCase().includes(query));
  }, [documents, fileLocations, folderId, query]);

  return { folders, files, isLoading };
};
